import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { api } from "@/lib/api";

interface OpsStatus {
  desktop: boolean;
  data_dir: string;
  vault_dir: string | null;
  backup_dir: string;
  last_backup_at: string | null;
}

const STALE_DAYS = 14;

/** Desktop-mode backup nudge: if the last backup is missing or stale, show
 * one toast per session with a one-click "Back up now" that zips the DB +
 * blobs into the vault's Backups/ folder. Silent in dev (desktop=false). */
export function useBackupReminder() {
  const shown = useRef(false);

  useEffect(() => {
    if (shown.current) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await api.request<OpsStatus>("GET", "/api/ops/status");
        if (cancelled || !status.desktop || shown.current) return;
        const last = status.last_backup_at ? new Date(status.last_backup_at) : null;
        const staleMs = STALE_DAYS * 24 * 60 * 60 * 1000;
        if (last && Date.now() - last.getTime() < staleMs) return;
        shown.current = true;
        toast("Back up your atlas?", {
          id: "backup-reminder",
          duration: 12_000,
          description: last
            ? `Last backup ${last.toLocaleDateString()} — a fresh snapshot lands in ${status.backup_dir}.`
            : `No backup yet — one click snapshots your database and files into ${status.backup_dir}.`,
          action: {
            label: "Back up now",
            onClick: () => {
              void api
                .request<{ archive: string }>("POST", "/api/ops/backup")
                .then((r) => toast.success("Backup saved", { description: r.archive }))
                .catch((e) => toast.error("Backup failed", { description: String(e) }));
            },
          },
        });
      } catch {
        /* status endpoint unavailable (older backend) — stay quiet */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}
